import { Radio, extendTheme } from "@chakra-ui/react";
import { radioTheme } from "./components/Radio";

const theme = extendTheme({
  config: {
    initialColorMode: "dark",
    useSystemColorMode: false,
  },
  colors: {
    brand: {
      50: "#F7F5FF",
      100: "#F4F0FF",
      200: "#EFEAFF",
      300: "#D6CBFF",
      400: "#C2B1FF",
      500: "#B5A0FF",
      600: "#A187FB",
      700: "#7652F7",
      800: "#582BFB",
      900: "#4A1FB5",
    },
    purple: {
      50: "#F7F5FF",
      100: "#F4F0FF",
      200: "#EFEAFF",
      300: "#D6CBFF",
      400: "#C2B1FF",
      500: "#9500FF",
      600: "#A187FB",
      700: "#7652F7",
      800: "#582BFB",
      900: "#4A1FB5",
    },
    accent: {
      500: "#FC7807",
    },
    gray: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
  },
  fonts: {
    heading: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`,
    body: `"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`,
  },
  fontSizes: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
    lg: "1.125rem",
    xl: "1.25rem",
    "2xl": "1.5rem",
    "3xl": "1.875rem",
    "4xl": "2.25rem",
    "5xl": "3rem",
    "6xl": "3.75rem",
    "7xl": "4.5rem",
    "8xl": "6rem",
    "9xl": "8rem",
  },
  fontWeights: {
    hairline: 100,
    thin: 200,
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    black: 900,
  },
  lineHeights: {
    normal: "normal",
    none: 1,
    shorter: 1.25,
    short: 1.375,
    base: 1.5,
    tall: 1.625,
    taller: "2",
  },
  space: {
    px: "1px",
    0.5: "0.125rem",
    1: "0.25rem",
    1.5: "0.375rem",
    2: "0.5rem",
    2.5: "0.625rem",
    3: "0.75rem",
    3.5: "0.875rem",
    4: "1rem",
    5: "1.25rem",
    6: "1.5rem",
    7: "1.75rem",
    8: "2rem",
    9: "2.25rem",
    10: "2.5rem",
    12: "3rem",
    14: "3.5rem",
    16: "4rem",
    20: "5rem",
    24: "6rem",
    28: "7rem",
    32: "8rem",
    36: "9rem",
    40: "10rem",
    44: "11rem",
    48: "12rem",
    52: "13rem",
    56: "14rem",
    60: "15rem",
    64: "16rem",
    72: "18rem",
    80: "20rem",
    96: "24rem",
  },
  styles: {
    global: (props: any) => ({
      body: {
        bg:
          props.colorMode === "dark"
            ? "linear-gradient(135deg, #1a0933 0%, #0a0a0a 50%, #1a0933 100%)"
            : "white",
        color: props.colorMode === "dark" ? "white" : "gray.900",
        minHeight: "100vh",
      },
      "#root": {
        background:
          props.colorMode === "dark"
            ? "linear-gradient(135deg, #1a0933 0%, #0a0a0a 50%, #1a0933 100%)"
            : "white",
        minHeight: "100vh",
      },
    }),
  },
  components: {
    Radio: radioTheme,
    Button: {
      baseStyle: {
        borderRadius: "20px",
        fontWeight: "600",
      },
      variants: {
        solid: (props: any) => ({
          bg: props.colorScheme === "brand" ? "brand.700" : undefined,
          color: "white",
          _hover: {
            bg: props.colorScheme === "brand" ? "brand.600" : undefined,
            transform: "translateY(-2px)",
            boxShadow: "0 10px 20px rgba(118, 82, 247, 0.2)",
          },
          transition: "all 0.2s",
        }),
        ghost: {
          _hover: {
            bg: "brand.100",
            transform: "translateY(-1px)",
          },
          transition: "all 0.2s",
        },
      },
    },
    Card: {
      baseStyle: (props: any) => ({
        container: {
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
          boxShadow: "0 10px 20px rgba(0, 0, 0, 0.1)",
          bg:
            props.colorMode === "dark" ? "rgba(255, 255, 255, 0.05)" : "white",
          border:
            props.colorMode === "dark"
              ? "1px solid rgba(255, 255, 255, 0.1)"
              : "1px solid rgba(0, 0, 0, 0.1)",
        },
      }),
    },
    Box: {
      baseStyle: (props: any) => ({
        color: props.colorMode === "dark" ? "white" : "gray.900",
      }),
    },
    Text: {
      baseStyle: (props: any) => ({
        color: props.colorMode === "dark" ? "white" : "gray.900",
      }),
    },
    Heading: {
      baseStyle: (props: any) => ({
        color: props.colorMode === "dark" ? "white" : "gray.900",
      }),
    },
  },
});

export default theme;
